import { Link, NavLink, useNavigate } from "react-router-dom";
import { Globe, LayoutDashboard, LogOut, Shield, User, ShieldCheck, ListMusic } from "lucide-react";
import { SearchBar } from "@/components/SearchBar";
import { useAuth } from "@/context/AuthContext";
import { useLang, useT, LANGS } from "@/lib/i18n";
import { mediaUrl } from "@/lib/api";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";

export const Logo = () => (
  <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 shrink-0">
    <span className="flex items-end gap-[3px] h-6">
      {[10, 18, 24, 14, 8].map((h, i) => <span key={i} className="w-[4px] rounded-full bg-[var(--hub)] transition-colors duration-500" style={{ height: h }} />)}
    </span>
    <span className="font-display text-2xl font-black tracking-tight">soundra</span>
  </Link>
);

const LangSwitch = () => {
  const { lang, setLang } = useLang();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger data-testid="language-switcher" className="flex items-center gap-1.5 text-xs font-mono uppercase text-zinc-300 hover:text-white px-2 py-1.5 rounded-full border border-white/10">
        <Globe size={14} /> {lang}
      </DropdownMenuTrigger>
      <DropdownMenuContent className="bg-[#12121A] border-white/10">
        {LANGS.map((l) => <DropdownMenuItem key={l} data-testid={`language-option-${l}`} onClick={() => setLang(l)} className="font-mono uppercase">{l}</DropdownMenuItem>)}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const UserMenu = () => {
  const { user, logout } = useAuth();
  const t = useT();
  const nav = useNavigate();
  if (user === null) return <div className="w-9 h-9" />;
  if (!user) return <Link to="/login" data-testid="nav-login-button" className="px-5 h-10 inline-flex items-center rounded-full bg-white text-black text-sm font-semibold hover:bg-zinc-200 transition-colors">{t("login")}</Link>;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger data-testid="user-menu-trigger" className="w-10 h-10 rounded-full overflow-hidden border border-white/15 bg-zinc-800 flex items-center justify-center font-display font-bold">
        {user.avatar ? <img src={mediaUrl(user.avatar)} alt="" className="w-full h-full object-cover" /> : user.name?.[0]}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-[#12121A] border-white/10 w-52">
        <div className="px-2 py-1.5 text-sm font-semibold truncate">{user.name}<div className="text-xs font-mono text-zinc-500 uppercase">{user.role} · {user.plan}</div></div>
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="menu-dashboard" onClick={() => nav("/dashboard")}><LayoutDashboard size={14} className="mr-2" />{t("dashboard")}</DropdownMenuItem>
        {user.role !== "listener" && <DropdownMenuItem data-testid="menu-profile" onClick={() => nav(`/artist/${user.slug}`)}><User size={14} className="mr-2" />{t("profile")}</DropdownMenuItem>}
        <DropdownMenuItem data-testid="menu-playlists" onClick={() => nav("/dashboard?tab=playlists")}><ListMusic size={14} className="mr-2" />My playlists</DropdownMenuItem>
        <DropdownMenuItem data-testid="menu-privacy" onClick={() => nav("/settings/privacy")}><Shield size={14} className="mr-2" />{t("privacy")}</DropdownMenuItem>
        {user.role === "admin" && <DropdownMenuItem data-testid="menu-admin" onClick={() => nav("/admin")}><ShieldCheck size={14} className="mr-2" />{t("admin")}</DropdownMenuItem>}
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); nav("/"); }}><LogOut size={14} className="mr-2" />{t("logout")}</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const Navbar = () => {
  const t = useT();
  const link = ({ isActive }) => `text-sm font-medium transition-colors ${isActive ? "text-white" : "text-zinc-400 hover:text-white"}`;
  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/70 border-b border-white/10">
      <div className="max-w-[1600px] mx-auto h-16 px-4 sm:px-6 flex items-center gap-4 lg:gap-8">
        <Logo />
        <nav className="hidden md:flex items-center gap-6">
          <NavLink to="/" end className={link} data-testid="nav-discover">{t("discover")}</NavLink>
          <NavLink to="/charts" className={link} data-testid="nav-charts">{t("charts")}</NavLink>
          <NavLink to="/pricing" className={link} data-testid="nav-pricing">{t("pricing")}</NavLink>
        </nav>
        <div className="flex-1 hidden md:flex justify-center min-w-0"><SearchBar /></div>
        <div className="flex-1 md:hidden" />
        <LangSwitch />
        <UserMenu />
      </div>
      <div className="md:hidden px-4 pb-3"><SearchBar /></div>
      <nav className="md:hidden flex gap-6 px-4 pb-2">
        <NavLink to="/" end className={link}>{t("discover")}</NavLink>
        <NavLink to="/charts" className={link}>{t("charts")}</NavLink>
        <NavLink to="/pricing" className={link}>{t("pricing")}</NavLink>
      </nav>
    </header>
  );
};
